import { ChangeEvent, forwardRef, useEffect, useState, Ref } from 'react';
import { compareFiles, downloadImage, getName } from '../../../utilities';
import { ImageTypeModel } from '../CreateMemorialQuiltStepThree/model';
import Cropper from 'cropperjs';
import { useParams } from 'react-router-dom';
import {
  deletePhotoFromQuiltAlbum,
  postPhotoToQuiltAlbum,
} from '../../../api/http-client/queries/quilts';
import { Loader } from '../../Loader/Loader';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { setEditableQuiltState } from '../../../store/reducers/quiltSlice';
import { editUnpublishedQuilt } from '../../../api/http-client/queries/quiltQueries';
import { setOpen } from 'store/reducers/notifySlice';
import { TImage } from '../../../api/http-client/types';
import { EDITED_IMAGE } from '../constants/constants';
import { checkAndParseImages } from '../helpers/check-and-parse-images';
import { ImageEditorBlock } from './ImageEditorBlock';

interface CreateImagesBlockProps {
  setImageBlock: (image: ImageTypeModel | null) => void;
  isAddImagesOnly?: boolean;
  isStepOne?: boolean;
  isMain?: boolean;
  imageName?: string | null;
  showAddImages?: boolean;
}

export const CreateImagesBlock = forwardRef<HTMLDivElement, CreateImagesBlockProps>(
  (
    { 
      setImageBlock, 
      isAddImagesOnly, 
      isStepOne, 
      isMain = false, 
      imageName, 
      showAddImages
    },
    ref: Ref<HTMLDivElement>
  ) => {
    const { id } = useParams();
    const editableQuiltStore = useAppSelector((state) => state.quilt.editableQuilt);
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const [filesImages, setFilesImages] = useState<File[]>([]);
    const [currentFileImage, setCurrentFileImage] = useState<File | null>(() => null);
    const [originalImage, setOriginalImage] = useState<string | null>(null);
    const [currentCrop, setCurrentCrop] = useState<Cropper | null>(null);
    const [showClearImagesDialog, setShowClearImagesDialog] = useState<boolean>(false);

    const initImages = async (data: TImage[]) => {
      const promisesFiles: Promise<File>[] = [];

      for (let i = 0; i < data.length; i++) {
        if (!(data[i].name.includes(EDITED_IMAGE))) {
          promisesFiles.push(downloadImage(data[i].full));
        }
      }

      const resultData = await Promise.all(promisesFiles);

      if (promisesFiles)
        setFilesImages(resultData);
    };

    const changeImages = async (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;

      if (!files || !id) return;

      // check the same images
      const filesDataUnique: File[] = [];
      const parsedImages = await checkAndParseImages(files);

      if (filesImages.length === 0) {
        filesDataUnique.push(...parsedImages);
      } else {
        for (const newFile of parsedImages) {
          let isNewFile = true;

          for (const wasFile of filesImages) {
            const isSameFile = await compareFiles(newFile, wasFile);

            if (isSameFile) {
              dispatch(
                setOpen(`You already have the same picture! You can\`t add ${newFile.name}`)
              );
              isNewFile = false;
              break;
            }
          }

          if (isNewFile) filesDataUnique.push(newFile);
        }
      }

      if (filesDataUnique.length > 0) {
        setLoading(true);
        await Promise.all(filesDataUnique.map(async (file) => postPhotoToQuiltAlbum(id, file)));

        updateEditableStore();

        if (isAddImagesOnly) {
          const name = getName({
            firstName: editableQuiltStore?.firstName,
            lastName: editableQuiltStore?.lastName,
          });

          // notify for only this page
          dispatch(setOpen(`Your images have been successfully added to ${name} album`));
        }

        setFilesImages((state) => {
          return [...state, ...filesDataUnique];
        });

        setLoading(false);
      }
    };

    const resetImages = () => {
      if (!id) return;

      setLoading(true);

      Promise.all(
        filesImages.map(async (fileImage) => await deletePhotoFromQuiltAlbum(id, fileImage.name))
      ).finally(() => {
        setLoading(false);
      });

      setFilesImages([]);
      setShowClearImagesDialog(false);
    };

    const deleteFileImage = async (fileIndex: number) => {
      if (!id) return;

      setLoading(true);

      // for local delete
      setFilesImages((state) =>
        state ? state.filter((item, index) => index !== fileIndex) : state
      );
      await deletePhotoFromQuiltAlbum(id, filesImages[fileIndex].name);

      setLoading(false);

      updateEditableStore();
    };

    const updateEditableStore = async () => {
      if (!id) return;

      setLoading(true);
      const data = await editUnpublishedQuilt(id);
      setLoading(false);

      // need for synchronization store with new albums
      dispatch(setEditableQuiltState(data));
    };

    useEffect(() => {
      (async () => {
        if (currentCrop !== null) {
          setCurrentFileImage(null);
          setCurrentCrop(null);
          setOriginalImage(null);

          currentCrop?.destroy();
        }

        if (!imageName) return;

        let imageUrl: string | undefined;

        if (isMain) {
          imageUrl = editableQuiltStore.album?.find((photo) =>
            photo.full.split('/').pop()?.endsWith(imageName)
          )?.full;
        } else {
          imageUrl = editableQuiltStore.album?.find(
            (photo) => photo.full.split('/').pop() === imageName
          )?.full;
        }

        if (!imageUrl) return;

        const downloadedImage = await downloadImage(imageUrl);
        setOriginalImage(imageName);
        setCurrentFileImage(downloadedImage);
      })();
    }, [imageName, showAddImages]);

    // init filesImages;
    useEffect(() => {
      if (editableQuiltStore?.album) {
        if (editableQuiltStore.album.length === 0) {
          return;
        }

        initImages(editableQuiltStore.album);
      }
    }, [editableQuiltStore?.album?.length]);

    return (
      <>
        <ImageEditorBlock 
          isLocal
          isAddImagesOnly={isAddImagesOnly}
          ref={ref}
          showClearImagesDialog={showClearImagesDialog}
          setShowClearImagesDialog={setShowClearImagesDialog}
          resetImages={resetImages}
          changeImages={changeImages}
          filesImages={filesImages}
          deleteFileImage={deleteFileImage}
          currentFileImage={currentFileImage}
          isStepOne={isStepOne}
          currentCrop={currentCrop}
          originalImage={originalImage}
          setImageBlock={setImageBlock}
          isMain={isMain}
          setCurrentCrop={setCurrentCrop}
          setCurrentFileImage={setCurrentFileImage}
          setOriginalImage={setOriginalImage}
        />
        <Loader loading={loading} />
      </>
    );
  }
);
